import { createDateFormatter, type TimezoneMode } from "@nexus/ui";
import type { BadgeTone } from "@nexus/ui";
import { profileLabel } from "@/config/uiConfig";
import type { Tenant } from "@/types";

/**
 * One vocabulary for "who reads what" in Administration › Users and Tenants.
 *
 * An account has one product profile. Client profiles read only the tenants their
 * scopes resolve to (direct tenants, every tenant of a customer, or all tenants);
 * internal NAGRA profiles read every tenant whatever their scopes. A new account is
 * created at first sign-in as a client viewer without scopes: it awaits setup.
 */

export interface AccessAccount {
  account_id: string;
  email: string;
  profile: string;
  scopes: string[];
  configured_profile_keys: string[] | null;
  configured_scope_keys: string[] | null;
  data_limited_to_scopes?: boolean;
  created_at?: string;
}

export interface Organization {
  key: string;
  label: string;
  tenant_ids: string[];
}

export type AccessKind = "client" | "internal";

export const CLIENT_PROFILE = "client-viewer";
export const INTERNAL_PROFILES = [
  "nagra-analyst",
  "nagra-ops",
  "nagra-officer",
  "nagra-admin",
] as const;

/** What each offered profile opens, in a few words. */
export const PROFILE_DESCRIPTIONS: Record<string, string> = {
  "client-viewer": "Sees the dashboards published to them, for their tenants only.",
  "nagra-analyst": "Live monitoring, journal and reporting.",
  "nagra-ops": "Analyst spaces, plus operations and investigations.",
  "nagra-officer": "Same spaces as Ops.",
  "nagra-admin": "Everything, including Administration.",
};

/** Short names for the internal role picker. */
export const ROLE_LABELS: Record<string, string> = {
  "nagra-analyst": "Analyst",
  "nagra-ops": "Ops",
  "nagra-officer": "Officer",
  "nagra-admin": "Admin",
};

export function kindOfProfile(profile: string): AccessKind {
  return (INTERNAL_PROFILES as readonly string[]).includes(profile) ? "internal" : "client";
}

/** Whether the account's data is bounded by its scopes (the API decides; profile as fallback). */
export function isScopeLimited(account: Pick<AccessAccount, "profile" | "data_limited_to_scopes">) {
  return account.data_limited_to_scopes ?? kindOfProfile(account.profile) === "client";
}

export interface ScopeParts {
  tenantKeys: string[];
  organizationKeys: string[];
  global: boolean;
  otherKeys: string[];
}

export function splitScopes(scopes: readonly string[]): ScopeParts {
  return {
    tenantKeys: scopes.filter((key) => key.startsWith("tenant:")),
    organizationKeys: scopes.filter((key) => key.startsWith("organization:")),
    global: scopes.some((key) => key.startsWith("global:")),
    otherKeys: scopes.filter((key) => !/^(tenant|organization|global):/.test(key)),
  };
}

export const tenantKey = (tenantId: string) => `tenant:${tenantId}`;
export const tenantIdOf = (key: string) => key.slice("tenant:".length);

/** True when a client account has nothing to read: the state of every new account. */
export function awaitsSetup(account: AccessAccount): boolean {
  if (!isScopeLimited(account)) return false;
  const parts = splitScopes(account.scopes);
  return !parts.global && parts.tenantKeys.length === 0 && parts.organizationKeys.length === 0;
}

export type AccountStatusKey = "setup" | "client" | "internal" | "admin";

export const STATUS_OPTIONS: { value: AccountStatusKey; label: string }[] = [
  { value: "setup", label: "Awaiting setup" },
  { value: "client", label: "Client" },
  { value: "internal", label: "Internal" },
  { value: "admin", label: "Administrator" },
];

export function accountStatus(account: AccessAccount): {
  key: AccountStatusKey;
  label: string;
  tone: BadgeTone;
} {
  if (account.profile === "nagra-admin")
    return { key: "admin", label: "Administrator", tone: "accent" };
  if (!isScopeLimited(account)) return { key: "internal", label: "Internal", tone: "neutral" };
  if (awaitsSetup(account)) return { key: "setup", label: "Awaiting setup", tone: "warning" };
  return { key: "client", label: "Client", tone: "neutral" };
}

/** "Client viewer", "NAGRA ops"… for a profile key. */
export const profileName = (profile: string) => profileLabel(profile);

export type TenantAccessVia =
  | { via: "direct" }
  | { via: "customer"; organization: Organization }
  | { via: "global" }
  | { via: "profile" };

/** How (and whether) an account reads one tenant. */
export function tenantAccess(
  account: AccessAccount,
  tenantId: string,
  organizations: readonly Organization[],
): TenantAccessVia | null {
  if (!isScopeLimited(account)) return { via: "profile" };
  const parts = splitScopes(account.scopes);
  if (parts.tenantKeys.includes(tenantKey(tenantId))) return { via: "direct" };
  const organization = organizations.find(
    (entry) => parts.organizationKeys.includes(entry.key) && entry.tenant_ids.includes(tenantId),
  );
  if (organization) return { via: "customer", organization };
  if (parts.global) return { via: "global" };
  return null;
}

export interface AccessItem {
  key: string;
  label: string;
  /** Present for a tenant, absent for a whole customer. */
  tenantId?: string;
}

export type AccessSummary =
  | { kind: "all"; reason: "profile" | "global" }
  | { kind: "none" }
  | { kind: "some"; items: AccessItem[] };

/** What an account reads, named for people: tenants and whole customers. */
export function accessSummary(
  account: AccessAccount,
  tenants: readonly Tenant[],
  organizations: readonly Organization[],
): AccessSummary {
  if (!isScopeLimited(account)) return { kind: "all", reason: "profile" };
  const parts = splitScopes(account.scopes);
  if (parts.global) return { kind: "all", reason: "global" };
  const items: AccessItem[] = [
    ...parts.organizationKeys.map((key) => ({
      key,
      label: `All ${organizations.find((entry) => entry.key === key)?.label ?? key.slice("organization:".length)} tenants`,
    })),
    ...parts.tenantKeys.map((key) => {
      const id = tenantIdOf(key);
      return {
        key,
        tenantId: id,
        label: tenants.find((tenant) => tenant.tenant_id === id)?.tenant_label ?? `Tenant ${id}`,
      };
    }),
  ];
  return items.length ? { kind: "some", items } : { kind: "none" };
}

// ─── Editing ─────────────────────────────────────────────────────────────────

/** What an administrator edits for one account: a profile and its data access. */
export interface AccessDraft {
  profile: string;
  tenantKeys: string[];
  organizationKeys: string[];
  global: boolean;
  /** Technical scopes the editor does not offer; always kept. */
  otherKeys: string[];
}

export function draftOf(account: AccessAccount): AccessDraft {
  const parts = splitScopes(account.scopes);
  return {
    profile: account.profile,
    tenantKeys: parts.tenantKeys,
    organizationKeys: parts.organizationKeys,
    global: parts.global,
    otherKeys: parts.otherKeys,
  };
}

export function draftScopes(draft: AccessDraft): string[] {
  return [
    ...draft.tenantKeys,
    ...draft.organizationKeys,
    ...(draft.global ? ["global:*"] : []),
    ...draft.otherKeys,
  ];
}

const sameSet = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((value) => right.includes(value));

export function draftChanged(account: AccessAccount, draft: AccessDraft): boolean {
  return draft.profile !== account.profile || !sameSet(draftScopes(draft), account.scopes);
}

/**
 * The identity-overrides body for a draft. Unchanged halves are sent back as
 * configured, so an inherited profile or a legacy tenant column is not rewritten.
 */
export function accessRequest(account: AccessAccount, draft: AccessDraft) {
  const scopes = draftScopes(draft);
  return {
    profile_keys:
      draft.profile === account.profile ? account.configured_profile_keys : [draft.profile],
    scope_keys: sameSet(scopes, account.scopes) ? account.configured_scope_keys : scopes,
  };
}

/** A client draft that reads nothing: allowed, but the person will see an empty product. */
export function draftReadsNothing(draft: AccessDraft): boolean {
  return (
    kindOfProfile(draft.profile) === "client" &&
    !draft.global &&
    draft.tenantKeys.length === 0 &&
    draft.organizationKeys.length === 0
  );
}

/** "3 users" / "1 user". */
export const countLabel = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** Accounts created in the last week are flagged as new. */
export function isRecent(account: Pick<AccessAccount, "created_at">, now = Date.now()) {
  if (!account.created_at) return false;
  const created = Date.parse(account.created_at);
  return Number.isFinite(created) && now - created < 7 * 24 * 60 * 60 * 1000;
}

/** "21 Oct 2026" for an account date; empty when absent or invalid. */
export function formatDay(value: string | undefined, timezone: TimezoneMode = "utc"): string {
  const time = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(time)
    ? createDateFormatter(timezone, { day: "numeric", month: "short", year: "numeric" }).format(
        time,
      )
    : "";
}
